/**
 * tridoo-scelta-immagine
 *
 * Collega le due direzioni fra galleria e selettori di variante:
 *   miniatura cliccata  →  i colori si selezionano da soli
 *   colori cambiati     →  l'anteprima accanto ai bottoni si aggiorna
 *
 * La seconda esiste per il telefono: i selettori stanno sotto la
 * descrizione e la galleria è uno schermo più su, quindi tocchi «Blu» e
 * non vedi succedere niente. La conferma deve arrivare dove sta il dito.
 *
 * Il tema fa il resto da solo: i suoi radio sono dentro <variant-selects>,
 * che ascolta l'evento `change` che sale dai figli. Qui si spuntano i
 * radio giusti e si manda UN solo `change` — non uno per opzione, o il
 * tema ricalcolerebbe prezzo, disponibilità e media tre volte di fila.
 *
 * Niente dipendenze dal bus interno del tema: solo DOM ed eventi standard,
 * così un aggiornamento di Hyper non porta via il pezzo con sé.
 */
(() => {
  if (customElements.get('tridoo-scelta-immagine')) return;

  customElements.define(
    'tridoo-scelta-immagine',
    class TridooSceltaImmagine extends HTMLElement {
      connectedCallback() {
        if (this._acceso) return;
        this._acceso = true;

        const grezzo = this.querySelector('[data-tabella]');
        if (!grezzo) return;
        try {
          this.tabella = JSON.parse(grezzo.textContent);
        } catch (err) {
          console.warn('[tridoo-scelta-immagine] tabella illeggibile', err);
          return;
        }
        if (!Array.isArray(this.tabella.media) || this.tabella.media.length === 0) return;

        // La taglia non si tocca: da una miniatura non si capisce quale
        // misura volesse chi ha cliccato, perché le misure di uno stesso
        // abbinamento condividono la foto.
        const nomeTaglia = (this.dataset.taglia || 'Taglia').trim().toLowerCase();
        this.indiciColore = this.tabella.opzioni
          .map((nome, i) => ({ nome: String(nome).trim().toLowerCase(), i }))
          .filter((o) => o.nome !== nomeTaglia)
          .map((o) => o.i);

        this.sezione = this.closest('[id^="shopify-section-"]') || document;
        this.combo = this.querySelector('[data-combo]');

        this.sezione.addEventListener('click', this.daMiniatura.bind(this));
        this.sezione.addEventListener('change', this.aggiornaStriscia.bind(this));

        /*
         * Il `change` da solo non basta.
         *
         * Quando si tocca un colore che rende impossibile l'altro, il tema
         * corregge la seconda opzione per conto suo e RIDISEGNA il picker —
         * senza emettere un altro `change`. Ascoltando solo l'evento, la
         * striscia leggerebbe lo stato di mezzo secondo prima e resterebbe
         * indietro per sempre, segnando un abbinamento che non è più quello
         * scelto.
         *
         * Guardare il DOM copre tutt'e due i casi e non dipende da come il
         * tema è fatto dentro. Nessun rischio di rincorsa: qui si toccano
         * solo classi sulla striscia, che sta fuori dal picker osservato.
         */
        const selettori = this.selettori;
        if (selettori && 'MutationObserver' in window) {
          this.osservatore = new MutationObserver(() => {
            clearTimeout(this._attesa);
            this._attesa = setTimeout(() => this.aggiornaStriscia(), 60);
          });
          this.osservatore.observe(selettori, { childList: true, subtree: true, attributes: true });
        }

        // Lo stato iniziale: senza questo la striscia non segna niente
        // finché non si tocca qualcosa, e sembra rotta.
        this.aggiornaStriscia();
      }

      disconnectedCallback() {
        if (this.osservatore) this.osservatore.disconnect();
        clearTimeout(this._attesa);
      }

      /** I <variant-selects> di questa sezione, se ci sono. */
      get selettori() {
        return this.sezione.querySelector ? this.sezione.querySelector('variant-selects') : null;
      }

      /** I valori d'opzione spuntati adesso, nell'ordine delle opzioni. */
      valoriScelti() {
        const selettori = this.selettori;
        if (!selettori) return [];
        return this.tabella.opzioni.map((_, i) => {
          const gruppo = this.gruppoOpzione(i);
          const scelto = gruppo.find((el) => el.checked);
          return scelto ? this.valoreDi(scelto) : null;
        });
      }

      /**
       * I radio di una singola opzione.
       *
       * Si raggruppa per l'attributo `name`, non per il valore: su questo
       * prodotto «Rosa» esiste sia nella Base sia nel Corpo superiore, e
       * cercare per valore in tutto il picker spunterebbe il bottone
       * dell'opzione sbagliata.
       */
      gruppoOpzione(indice) {
        const selettori = this.selettori;
        if (!selettori) return [];
        const tutti = Array.from(selettori.querySelectorAll('input[type="radio"]'));
        const nomi = [];
        for (const el of tutti) {
          if (!nomi.includes(el.name)) nomi.push(el.name);
        }
        const nome = nomi[indice];
        return nome ? tutti.filter((el) => el.name === nome) : [];
      }

      valoreDi(input) {
        return (input.value || '').trim();
      }

      /**
       * Foto cliccata → spunta i colori di quella combinazione.
       *
       * Due sorgenti, stesso gesto: le miniature della galleria del tema
       * (`data-target`) e le voci della striscia qui sotto (`data-media`),
       * che su telefono sono le sole che si vedono senza risalire la
       * pagina.
       */
      daMiniatura(evento) {
        const bersaglioClick = evento.target.closest
          ? evento.target.closest('[data-media], [data-target]')
          : null;
        if (!bersaglioClick) return;
        const id = String(bersaglioClick.dataset.media || bersaglioClick.dataset.target || '');
        const voce = this.tabella.media.find((m) => String(m.id) === id);
        // Le foto che non appartengono a nessuna variante — dimensionale,
        // schede A+ — non sono nella tabella: cliccarle non fa niente.
        if (!voce) return;

        let ultimo = null;
        for (const i of this.indiciColore) {
          const atteso = String(voce.valori[i] || '').trim();
          if (!atteso) continue;
          const gruppo = this.gruppoOpzione(i);
          const bersaglio = gruppo.find((el) => this.valoreDi(el) === atteso);
          if (!bersaglio || bersaglio.checked) continue;
          bersaglio.checked = true;
          ultimo = bersaglio;
        }
        // Un solo evento per tutte le opzioni cambiate: il tema ricalcola
        // una volta invece di tre.
        if (ultimo) ultimo.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Colori cambiati → la striscia segna quello scelto e se lo porta
       * sotto gli occhi.
       *
       * Vale anche quando il colore lo si cambia dai bottoni o dalla
       * galleria vera: la striscia è uno specchio dello stato, non una
       * seconda verità.
       */
      aggiornaStriscia() {
        if (!this.combo) return;
        const scelti = this.valoriScelti();
        if (scelti.length === 0) return;

        const voce = this.tabella.media.find((m) =>
          this.indiciColore.every((i) => String(m.valori[i] || '').trim() === String(scelti[i] || '').trim()),
        );

        const etichetta = this.indiciColore.map((i) => scelti[i]).filter(Boolean).join(' / ');
        const nome = this.combo.querySelector('[data-combo-nome]');
        if (nome) nome.textContent = etichetta;

        // Il riquadro grande: è lui che risponde alla domanda «di che
        // colore viene», la striscia serve solo a cambiarlo.
        const grande = this.combo.querySelector('[data-combo-grande]');
        if (grande) {
          if (voce && voce.src) {
            grande.src = voce.src;
            grande.alt = etichetta;
            grande.hidden = false;
          } else {
            // Combinazione che non ha una foto propria: meglio il vuoto
            // che la foto di un altro abbinamento.
            grande.hidden = true;
          }
        }

        let attiva = null;
        for (const bottone of this.combo.querySelectorAll('[data-media]')) {
          const suo = voce && String(bottone.dataset.media) === String(voce.id);
          bottone.classList.toggle('is-scelta', !!suo);
          bottone.setAttribute('aria-pressed', suo ? 'true' : 'false');
          if (suo) attiva = bottone;
        }

        this.combo.hidden = false;

        // Portarla in vista scorrendo solo la striscia. scrollIntoView, anche
        // con `nearest`, fa scorrere la pagina se la voce è fuori schermo: al
        // caricamento portava la scheda 900px più giù, sotto il riquadro.
        if (attiva) {
          let striscia = attiva.parentElement;
          while (striscia && striscia !== this.combo && striscia.scrollWidth <= striscia.clientWidth + 1) {
            striscia = striscia.parentElement;
          }
          if (striscia && striscia.scrollWidth > striscia.clientWidth + 1) {
            const a = attiva.getBoundingClientRect();
            const s = striscia.getBoundingClientRect();
            const sinistra = striscia.scrollLeft + (a.left - s.left) - (striscia.clientWidth - a.width) / 2;
            striscia.scrollTo({ left: Math.max(0, sinistra), behavior: 'smooth' });
          }
        }
      }
    },
  );
})();
